/**
 * Profile: gear opens full settings page (sidebar + panels). Same structure as plugin/lectura.
 */

import { createEl } from '../core/dom';
import type { AuthService, ChatRoleService } from '../core/types';
import { createSettingsPage, type SettingsSectionId } from './settings-page';

export interface ProfileSlots {
  profileSlot?: HTMLElement;
}

export interface ProfileOptions {
  getSlidesVersion?: () => string;
  roleService?: ChatRoleService;
  presentationService?: import('../services/presentation-firestore').PresentationManagementService;
  getPresentationId?: () => string;
  /** Hide settings nav + panels (e.g. `['tts','shortcuts']` on remote). */
  excludeSettingsSections?: ReadonlyArray<SettingsSectionId>;
}

export interface ProfileApi {
  openSettings: (section?: SettingsSectionId) => void;
}

export function registerProfile(
  authService: AuthService,
  slots: ProfileSlots = {},
  options: ProfileOptions = {}
): ProfileApi | undefined {
  const profileSlot = slots.profileSlot ?? document.querySelector<HTMLElement>('.lectura-bar-profile');
  if (!profileSlot) return undefined;

  const gear = createEl('button', {
    type: 'button',
    className: 'lectura-bar-btn lectura-bar-settings-btn',
    'aria-label': 'Settings',
    title: 'Settings (?)'
  });
  gear.innerHTML =
    '<svg class="lectura-bar-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>';
  profileSlot.appendChild(gear);

  let settingsPageApi: ReturnType<typeof createSettingsPage> | null = null;
  let pageEl: HTMLElement | null = null;
  // Mount settings outside `.reveal` so Reveal theme CSS can't affect it.
  const mountTarget = document.body;

  function closePage(): void {
    if (settingsPageApi) settingsPageApi.hide();
  }

  function openSettings(section?: SettingsSectionId): void {
    if (!settingsPageApi) {
      settingsPageApi = createSettingsPage({
        authService,
        roleService: options.roleService,
        presentationService: options.presentationService,
        getPresentationId: options.getPresentationId,
        getSlidesVersion: options.getSlidesVersion,
        defaultSection: 'appearance',
        excludeSections: options.excludeSettingsSections,
        onClose: closePage
      });
      pageEl = settingsPageApi.getPage();
      mountTarget.appendChild(pageEl);
    }
    settingsPageApi.show(section);
  }

  gear.addEventListener('click', (e: Event) => {
    e.stopPropagation();
    openSettings();
  });

  return { openSettings };
}
